import type { DuplicateCandidate, BulkMergeGroupRequest } from "../../../../types/api";

/**
 * Pure helpers behind the Merge Manager's selection and bulk-merge confirmation.
 * Kept out of the component so the irreversible-action rules are directly testable.
 */

/** Cap the nav badge the same way mobile's `CountBadge` does. */
export const formatBadgeCount = (count: number): string =>
  count > 99 ? "99+" : String(count);

/**
 * Groups per bulk-merge request. Selecting all of a large organization can mean
 * hundreds of groups, and the server merges each in its own transaction, so the
 * run is split into batches: bounded request time, and a network failure costs
 * one batch instead of the whole run.
 */
export const BULK_MERGE_BATCH_SIZE = 25;

export const chunk = <T,>(items: T[], size: number): T[][] => {
  if (size < 1) return items.length > 0 ? [items] : [];
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
};

export const toggleSelection = (selected: Set<string>, candidateId: string): Set<string> => {
  const next = new Set(selected);
  if (next.has(candidateId)) {
    next.delete(candidateId);
  } else {
    next.add(candidateId);
  }
  return next;
};

/** Drop ids that are no longer loaded, so a stale selection can't be submitted. */
export const pruneSelection = (
  selected: Set<string>,
  loaded: DuplicateCandidate[]
): Set<string> => {
  const loadedIds = new Set(loaded.map((candidate) => candidate.id));
  return new Set([...selected].filter((id) => loadedIds.has(id)));
};

/**
 * The record that survives a bulk merge: the oldest one, matching the server's
 * rule. Ties break on prospect id so the client and server agree.
 */
export const pickBulkSurvivor = (candidate: DuplicateCandidate) =>
  [...candidate.members].sort((a, b) => {
    const byCreated = Date.parse(a.createdAt) - Date.parse(b.createdAt);
    if (byCreated !== 0) return byCreated;
    return a.prospectId.localeCompare(b.prospectId);
  })[0];

/**
 * What the confirmation dialog states before an irreversible run: how many groups,
 * how many player records disappear, and how many carry a tag value that would be
 * silently dropped.
 */
export type BulkMergeSummary = {
  groupCount: number;
  /** Records merged away — members minus one survivor per group. */
  playersAffected: number;
  tagConflictCount: number;
};

export const summarizeBulkMerge = (candidates: DuplicateCandidate[]): BulkMergeSummary => ({
  groupCount: candidates.length,
  playersAffected: candidates.reduce(
    (total, candidate) => total + Math.max(candidate.members.length - 1, 0),
    0
  ),
  tagConflictCount: candidates.filter((candidate) => candidate.hasTagConflict).length,
});

/**
 * Build the request. The survivor is sent explicitly so the coach's confirmation
 * and the server's merge cannot disagree about which record survives.
 */
export const buildBulkMergeGroups = (
  candidates: DuplicateCandidate[]
): BulkMergeGroupRequest[] =>
  candidates.map((candidate) => {
    const survivor = pickBulkSurvivor(candidate);
    return {
      candidateId: candidate.id,
      survivingProspectId: survivor?.prospectId,
      mergedProspectIds: candidate.members
        .filter((member) => member.prospectId !== survivor?.prospectId)
        .map((member) => member.prospectId),
    };
  });

/** Human-readable chip labels for the `matchedOn` attributes. */
const MATCH_LABELS: Record<string, string> = {
  name: "Name",
  gradYear: "Grad year",
  position: "Position",
  team: "Team",
};

export const matchLabel = (attribute: string): string =>
  MATCH_LABELS[attribute] ?? attribute;

export const memberName = (member: { firstName: string; lastName: string }): string =>
  `${member.firstName} ${member.lastName}`.trim();
